import { useState, useCallback } from 'react'
import axios from 'axios'
import { openAIUrl } from 'urls'
import { getAxiosDefaultConfig } from 'utils'

interface UseVoiceTranscriptionReturn {
  transcript: string
  isTranscribing: boolean
  error: string | null
  transcribe: (audioBlob: Blob) => Promise<string | null>
  resetTranscript: () => void
}

export const useVoiceTranscription = (): UseVoiceTranscriptionReturn => {
  const [transcript, setTranscript] = useState('')
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const transcribe = useCallback(async (audioBlob: Blob): Promise<string | null> => {
    if (!audioBlob || audioBlob.size === 0) {
      setError('No audio to transcribe')
      return null
    }

    setIsTranscribing(true)
    setError(null)

    try {
      const formData = new FormData()
      // Determine file extension based on MIME type
      const mimeType = audioBlob.type
      let extension = 'webm'
      if (mimeType.includes('mp4')) extension = 'mp4'
      else if (mimeType.includes('wav')) extension = 'wav'
      else if (mimeType.includes('ogg')) extension = 'ogg'

      formData.append('audio', audioBlob, `recording.${extension}`)

      const config = getAxiosDefaultConfig()
      const response = await axios.post(`${openAIUrl}/voice/transcribe`, formData, {
        ...config,
        headers: {
          ...config.headers,
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 second timeout
      })

      const text = response.data.text || ''
      setTranscript(text)
      return text
    } catch (err: any) {
      console.error('[useVoiceTranscription] Error:', err)

      let errorMessage = 'Failed to transcribe audio'
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error
      } else if (err.message === 'Network Error') {
        errorMessage = 'Network error. Please check your connection.'
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'Transcription timed out. Please try again.'
      }

      setError(errorMessage)
      return null
    } finally {
      setIsTranscribing(false)
    }
  }, [])

  const resetTranscript = useCallback(() => {
    setTranscript('')
    setError(null)
  }, [])

  return {
    transcript,
    isTranscribing,
    error,
    transcribe,
    resetTranscript,
  }
}

export default useVoiceTranscription
